import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { isLayoutType } from './fieldTypes';

// One line of text per stored answer (build plan §2.5 "Flattening"), used by
// the submissions table, the entry drawer and the CSV. Must produce the same
// strings as the server's shared/form-builder/flatten.js. Always pass the
// field from the submission's own version so labels and options match what
// the person saw.

const str = (v) => (v == null ? '' : String(v));
const clean = (v) => str(v).trim();
const joinNonBlank = (parts, sep) => parts.map(clean).filter(Boolean).join(sep);

const formatName = (value) => {
  const v = value || {};
  return joinNonBlank([v.first, v.middle, v.last], ' ');
};

const formatAddress = (value) => {
  const v = value || {};
  const lines = joinNonBlank([v.street, v.street2, v.city], ', ');
  const region = joinNonBlank([v.state, v.zip], ' ');
  return [lines, region].filter(Boolean).join(', ');
};

// +1 with 10 digits → "(704) 555-0142"; anything else → the international
// format, "+44 7400 123456" ("+999 123" when libphonenumber can't read it).
const formatPhone = (value) => {
  if (!value || typeof value !== 'object') return clean(value);
  const cc = clean(value.cc) || '+1';
  const digits = str(value.number).replace(/\D/g, '');
  if (!digits) return '';
  if (cc === '+1' && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return formatPhoneNumberIntl(cc + digits) || `${cc} ${digits}`;
};

const formatDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean(value));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : clean(value);
};

const formatCheckbox = (field, value) => {
  const single = !!(field.config && field.config.single);
  if (single) return value === true ? 'Agreed' : '';
  return Array.isArray(value) ? joinNonBlank(value, '; ') : '';
};

const columnLabel = (columns, index) =>
  (columns && clean(columns[index])) || `Column ${index + 1}`;

// "Item: Tires, Qty: 4" for one row's non-blank cells, in column order.
const formatRow = (columns, cells) =>
  cells
    .filter(([, text]) => clean(text))
    .sort((a, b) => a[0] - b[0])
    .map(([col, text]) => `${columnLabel(columns, col)}: ${clean(text)}`)
    .join(', ');

const formatTable = (field, value) => {
  if (!value || typeof value !== 'object') return '';
  const columns = field.config && field.config.columns;
  const rows = {};
  Object.keys(value).forEach((key) => {
    const m = /^(\d+)-(\d+)$/.exec(key);
    if (!m) return;
    const row = Number(m[1]);
    if (!rows[row]) rows[row] = [];
    rows[row].push([Number(m[2]), value[key]]);
  });
  return Object.keys(rows)
    .map(Number)
    .sort((a, b) => a - b)
    .map((row) => formatRow(columns, rows[row]))
    .filter(Boolean)
    .join(' | ');
};

const formatRepeater = (field, value) => {
  if (!Array.isArray(value)) return '';
  const columns = field.config && field.config.columns;
  return value
    .map((entry) =>
      entry && typeof entry === 'object'
        ? formatRow(
            columns,
            Object.keys(entry)
              .filter((k) => /^\d+$/.test(k))
              .map((k) => [Number(k), entry[k]])
          )
        : ''
    )
    .filter(Boolean)
    .join(' | ');
};

const formatMatrix = (field, value) => {
  if (!value || typeof value !== 'object') return '';
  const rows = (field.config && field.config.rows) || [];
  return rows
    .filter((row) => clean(value[row]))
    .map((row) => `${row}: ${clean(value[row])}`)
    .join('; ');
};

const formatRating = (field, value) => {
  if (value == null || value === '') return '';
  const max = (field.config && field.config.max) || 5;
  return `${value}/${max}`;
};

// ---- W-9 system types (w9-plan §2.4 "Flattening") ----------------------

// The stored tin answer is { kind: 'SSN' | 'EIN', last4 }: the full number
// never reaches `answers`, so a flattened TIN is always masked.
export const maskTin = (kind, last4) =>
  kind === 'EIN' ? `**-***${last4}` : `***-**-${last4}`;

export const formatTin = (value) => {
  if (!value || typeof value !== 'object') return '';
  const kind = clean(value.kind).toUpperCase();
  const last4 = str(value.last4).replace(/\D/g, '');
  if ((kind !== 'SSN' && kind !== 'EIN') || last4.length !== 4) return '';
  return `${kind} · ${maskTin(kind, last4)}`;
};

export const SIGNATURE_MODE_LABELS = { typed: 'typed', drawn: 'drawn' };

export const formatSignature = (value) => {
  if (!value || typeof value !== 'object') return '';
  const name = clean(value.name);
  if (!name) return '';
  const mode = SIGNATURE_MODE_LABELS[value.mode];
  return mode ? `Signed by ${name} (${mode})` : `Signed by ${name}`;
};

// Frank's values → the IRS box labels (w9-plan §2.2 row 6).
export const TAX_CLASS_LABELS = {
  individual: 'Individual / Sole proprietor',
  c_corporation: 'C Corporation',
  s_corporation: 'S Corporation',
  partnership: 'Partnership',
  trust_estate: 'Trust / Estate',
  llc: 'Limited Liability Company (LLC)',
  other: 'Other',
};

export const FOREIGN_PARTNERS_SUFFIX = ' · foreign partners';

export const formatTaxClass = (value) => {
  if (!value || typeof value !== 'object') return '';
  const classification = clean(value.classification);
  if (!TAX_CLASS_LABELS[classification]) return '';
  let text = TAX_CLASS_LABELS[classification];
  if (classification === 'llc') {
    const code = clean(value.llc_code).toUpperCase();
    text = code ? `LLC · ${code}` : text;
  } else if (classification === 'other') {
    const other = clean(value.other);
    text = other ? `Other · ${other}` : text;
  }
  return value.foreign_partners === true
    ? `${text}${FOREIGN_PARTNERS_SUFFIX}`
    : text;
};

const FORMATTERS = {
  name: (_f, v) => formatName(v),
  address: (_f, v) => formatAddress(v),
  phone: (_f, v) => formatPhone(v),
  datetime: (_f, v) => formatDate(v),
  checkbox: formatCheckbox,
  rating: formatRating,
  file_upload: (_f, v) => (v && typeof v === 'object' ? clean(v.filename) : ''),
  table: formatTable,
  repeater: formatRepeater,
  matrix: formatMatrix,
  tin: (_f, v) => formatTin(v),
  signature: (_f, v) => formatSignature(v),
  tax_class: (_f, v) => formatTaxClass(v),
};

// The answer as one string ('' when empty or not an answer field).
export const formatAnswer = (field, value) => {
  if (!field || isLayoutType(field.type) || value == null) return '';
  const formatter = FORMATTERS[field.type];
  if (formatter) return formatter(field, value);
  return typeof value === 'object' ? '' : clean(value);
};

// For screens: the formatted answer, or an em dash when there is none.
export const displayAnswer = (field, value) =>
  formatAnswer(field, value) || '—';
